import { UUID } from "@bitory-gmbh/nidavellir";
import {
  CommonDialogFooter,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RefreshCw } from "lucide-react";
import t from "@/lang/lang";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useRelationshipsRelationshipRePopulateSeriesMutation } from "@/services/backend/relationships/api/service";
import { useEffect, useState } from "react";
import { DateInput } from "@/shared/components/form/date-input";
import {
  Commitment,
  nextCycleDate,
} from "@/services/backend/relationships/types/relationship";
import { toast } from "sonner";

export function RePopulateSeriesDialog({
  relationshipId,
  commitment,
}: {
  relationshipId: UUID;
  commitment: Commitment;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState({
    id: relationshipId,
    commitmentId: commitment.id,
    firstDate: nextCycleDate(commitment),
  });
  const [post, { isLoading, error, isSuccess, reset }] =
    useRelationshipsRelationshipRePopulateSeriesMutation();

  useEffect(() => {
    if (isSuccess) {
      reset();
      toast.success("Serie neu befüllt");
      setOpen(false);
    }
  }, [isSuccess, reset]);

  const doReCreate = () => {
    if (isLoading) {
      return;
    }

    post(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={RefreshCw} label={t("Serie neu befüllen")} />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Serie neu befüllen")}</DialogTitle>
          <DialogDescription>
            {t("Die Serie wird neu befüllt.")}
          </DialogDescription>
          <div className="text-muted-foreground text-sm">
            <div>{t("Dazu werden:")}</div>
            <ul className="list-disc pl-5">
              <li>
                {t("Alle zukünftigen, nicht erfüllten Sollstellungen gelöscht")}
              </li>
              <li>
                {t(
                  "Nur neue Sollstellungen nach dem angegebenen Datum erstellt",
                )}
              </li>
              <li>
                {t(
                  "Der Turnus und die Behandlung der aktuellen Sollstellung genutzt",
                )}
              </li>
            </ul>
          </div>
        </DialogHeader>
        <DateInput
          label={t("Erstes Datum")}
          date={request.firstDate}
          onDateChange={(firstDate) =>
            setRequest({ ...request, firstDate: firstDate ?? "" })
          }
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doReCreate} />
      </DialogContent>
    </Dialog>
  );
}
